import * as React from "react";
import { formatRelative, useShowTechnical } from "@ozpanel/ui";
import { StatusPill } from "./PageHeader";
import { lasted, noticeBadge, type FeedGroup } from "./notificationGroups";

// One folded line of the notification feed, the same in the bell and on the page: what is OPEN is
// shown in full; what ENDED says when, after how long and what the recovery said, and is dimmed —
// it is history, not a thing to act on.
//
// "In full" is the title, one line of what it costs and one of what to do. The explanation is behind
// "details" (owner, 2026-10-26: "THERE IS JUST TOO MUCH TEXT IN THE NOTIFICATIONS ... PUT USEFUL
// INFORMATION") — kept, never the headline.
export function NotificationLines({
  groups,
  onRead,
  empty,
  audience = "operator",
}: {
  groups: FeedGroup[];
  onRead?: (ids: number[]) => void;
  empty?: React.ReactNode;
  /** "customer": server names are a technical detail (decision 2026-11-03) — taken out of the words
   *  and the line's node list unless the viewer turned technical details on. */
  audience?: "operator" | "customer";
}) {
  const technical = useShowTechnical();
  const hideNodes = audience === "customer" && !technical;
  if (groups.length === 0) return <>{empty ?? null}</>;
  const say = (text: string | undefined, nodes: string[]) => (hideNodes && text ? withoutNodes(text, nodes) : text);
  return (
    <div className="divide-y divide-line">
      {groups.map((g) => {
        const badge = noticeBadge(g);
        // A recovery is the newest word on an incident — never dimmed as history, never unread.
        const dim = !g.recovery && (g.state === "ended" || !g.unread);
        return (
          <div key={g.key} className={"px-3 py-2.5 " + (dim ? "opacity-60" : "")}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <StatusPill tone={badge.tone}>{badge.label}</StatusPill>
                <span className="truncate text-[11.5px] text-muted">
                  {g.module}
                  {/* The nodes matter: one node saying it and every node saying it are different faults. */}
                  {g.nodes.length > 0 && !hideNodes && <span className="ml-1">· {g.nodes.join(", ")}</span>}
                  {g.times > 1 && <span className="ml-1">· said {g.times}×</span>}
                </span>
              </div>
              <span className="shrink-0 text-[11px] text-muted">{formatRelative(g.state === "open" ? g.startedAt : g.lastAt)}</span>
            </div>
            <div className="mt-1 text-[12.5px] font-medium leading-snug">{say(g.summary, g.nodes)}</div>
            {g.state === "ended" ? (
              <div className="mt-1 text-[11.5px] leading-relaxed text-muted">
                Ended {formatRelative(g.endedAt)}
                {lasted(g.startedAt, g.endedAt) && <> · lasted {lasted(g.startedAt, g.endedAt)}</>}
                {g.endedNote && <> — {say(g.endedNote, g.nodes)}</>}
              </div>
            ) : (
              <>
                {g.consequence && <div className="mt-0.5 text-[11.5px] leading-relaxed text-muted">{say(g.consequence, g.nodes)}</div>}
                {g.next && (
                  <div className="mt-0.5 text-[11.5px] leading-relaxed">
                    <span className="text-muted">Next:</span> {say(g.next, g.nodes)}
                  </div>
                )}
              </>
            )}
            {g.detail && (
              <details className="mt-1 text-[11.5px] text-muted">
                <summary className="cursor-pointer select-none hover:text-fg">details</summary>
                <div className="mt-1 whitespace-pre-line leading-relaxed">{say(g.detail, g.nodes)}</div>
              </details>
            )}
            {g.unread && !g.recovery && onRead && (
              <button type="button" className="mt-1.5 text-[11px] text-muted hover:text-fg" onClick={() => onRead(g.ids)}>
                mark read
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// The words of a notice without the server names it carries: "is unhealthy on node100" reads "is
// unhealthy", and a name left mid-sentence reads "a server". Only the names the notice itself lists.
export function withoutNodes(text: string, nodes: string[]): string {
  let out = text;
  for (const n of nodes) {
    if (!n) continue;
    const esc = n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    out = out.replace(new RegExp(`\\s+on\\s+${esc}\\b`, "g"), "").replace(new RegExp(`\\b${esc}\\b`, "g"), "a server");
  }
  return out;
}
